import React, { useMemo } from "react";
import Icon from "../../../components/AppIcon";

const parseMoney = (value) => {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;
    if (Number.isInteger(value)) return value / 100;
    return value;
  }

  const s = String(value).trim();
  if (!s) return 0;

  if (/^-?\d+$/.test(s)) return Number(s) / 100;

  const normalized = s.replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Math.abs(Number(value || 0)));

const isPaidStatus = (status) => {
  const s = String(status || "").toLowerCase();
  return (
    s === "paid" ||
    s === "pago" ||
    s === "baixado" ||
    s === "settled" ||
    s === "received" ||
    s === "recebido"
  );
};

const Card = ({ icon, label, value, sub }) => (
  <div className="bg-card rounded-lg border border-border p-6 card-shadow">
    <div className="flex items-start justify-between">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-accent/50">
        <Icon name={icon} size={18} />
      </div>
    </div>

    <div className="mt-4">
      <div className="text-2xl font-heading-semibold text-card-foreground">{value}</div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
      {sub ? <div className="text-xs text-muted-foreground mt-2">{sub}</div> : null}
    </div>
  </div>
);

const ReceivableKPIs = ({ analytics, items = [], isLoading }) => {
  const computed = useMemo(() => {
    const list = Array.isArray(items) ? items : [];

    const totalAbertoFromItems = list
      .filter((i) => !isPaidStatus(i?.status) && String(i?.status || "").toLowerCase() !== "canceled")
      .reduce((s, i) => s + parseMoney(i?.amount ?? i?.valor ?? 0), 0);

    const totalBaixadoFromItems = list
      .filter((i) => isPaidStatus(i?.status))
      .reduce((s, i) => s + parseMoney(i?.amount ?? i?.valor ?? 0), 0);

    const vencidosFromItems = list
      .filter((i) => {
        const days = Number(i?.daysToDue);
        if (!Number.isFinite(days)) return false;
        if (isPaidStatus(i?.status)) return false;
        if (String(i?.status || "").toLowerCase() === "canceled") return false;
        return days < 0;
      })
      .reduce((s, i) => s + parseMoney(i?.amount ?? i?.valor ?? 0), 0);

    const future30FromItems = list
      .filter((i) => {
        const days = Number(i?.daysToDue);
        if (!Number.isFinite(days)) return false;
        if (isPaidStatus(i?.status)) return false;
        if (String(i?.status || "").toLowerCase() === "canceled") return false;
        return days >= 0 && days <= 30;
      })
      .reduce((s, i) => s + parseMoney(i?.amount ?? i?.valor ?? 0), 0);

    // aceita vários formatos de analytics (alguns back mandam totals)
    const totalAberto =
      parseMoney(analytics?.totals?.totalOpen) ||
      parseMoney(analytics?.totalAberto) ||
      totalAbertoFromItems;

    const totalBaixado =
      parseMoney(analytics?.totals?.totalReceived) ||
      parseMoney(analytics?.totalBaixado) ||
      totalBaixadoFromItems;

    const vencidos =
      parseMoney(analytics?.totals?.totalOverdue) ||
      parseMoney(analytics?.totalVencido) ||
      vencidosFromItems;

    const proj30 =
      parseMoney(analytics?.projecao30Dias) ||
      future30FromItems;

    const clientes = new Set(
      list
        .map((i) => String(i?.customer || i?.clientName || i?.cliente || "").trim())
        .filter(Boolean)
    ).size;

    return { totalAberto, totalBaixado, vencidos, proj30, clientes };
  }, [analytics, items]);

  const loading = !!isLoading;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
      <Card
        icon="DollarSign"
        label="Total em Aberto"
        value={loading ? "—" : formatCurrency(computed.totalAberto)}
        sub={loading ? null : `${computed.clientes} cliente(s) no período`}
      />

      <Card
        icon="CheckCircle"
        label="Total Baixado"
        value={loading ? "—" : formatCurrency(computed.totalBaixado)}
        sub="recebimentos concluídos"
      />

      <Card
        icon="AlertCircle"
        label="Vencidos (em atraso)"
        value={loading ? "—" : formatCurrency(computed.vencidos)}
        sub="títulos vencidos e não baixados"
      />

      <Card
        icon="TrendingUp"
        label="Projeção (30 dias)"
        value={loading ? "—" : formatCurrency(computed.proj30)}
        sub="a vencer nos próximos 30 dias"
      />
    </div>
  );
};

export default ReceivableKPIs;
